import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../../../auth/AuthContext";
import { getFundDbName } from "../../../../../utils/funds";
import { companyService } from "../../../../company-management/pages/company-registration/services/company-service";
import { entregoCheckoutService } from "../services/entrego-checkout-service";
import { EntregoCheckoutItem } from "../types";

export const entregoCheckoutKeys = {
  all: ["entrego-checkout"] as const,
  list: (dbName: string) =>
    [...entregoCheckoutKeys.all, "list", dbName] as const,
  franquiasPausadas: () =>
    [...entregoCheckoutKeys.all, "franquias-pausadas"] as const,
};

export function useEntregoCheckout() {
  const { user } = useAuth();
  const xamaDbName = getFundDbName("entrego");

  const {
    data: checkoutList = [],
    isPending,
    refetch,
  } = useQuery({
    queryKey: entregoCheckoutKeys.list(xamaDbName),
    queryFn: async (): Promise<EntregoCheckoutItem[]> => {
      const response = await entregoCheckoutService.getCheckoutList({
        dbName: xamaDbName,
        opsMae: "entrego",
      });

      // A listagem já traz os arquivos processados agregados por data, então uma
      // única query cobre a tela inteira. Antes isso era uma segunda leva de N
      // requests num useEffect com estado próprio, e a tela exibia "não há
      // checkouts" durante toda essa janela.
      return (response.data || []).map(
        ({
          files_processed,
          resumo_descricao,
          ...checkout
        }): EntregoCheckoutItem => ({
          ...checkout,
          filesProcessed: files_processed || [],
          resumoDescricao: resumo_descricao || [],
        })
      );
    },
    staleTime: 1000 * 60 * 2,
    refetchInterval: 1000 * 120,
  });

  // Franquias com oferta pausada (cadastro_franquia_entrego.oferta_ativa = false):
  // o checkout não envia para elas. Mostradas na confirmação do WhatsApp.
  const { data: franquiasPausadas = [] } = useQuery({
    queryKey: entregoCheckoutKeys.franquiasPausadas(),
    queryFn: async (): Promise<string[]> => {
      const response = await companyService.getCompanies({ dbName: "xama" });
      return (response.data?.data || [])
        .filter((f) => f.oferta_ativa === false)
        .map((f) => f.ops);
    },
  });

  // TEMP-SPLIT-OFERTA: após o registro o backend grava status="confirmed", mas a
  // leitura da listagem pode levar alguns segundos para refletir. Refazemos a busca
  // repetidas vezes até o item aparecer com o status esperado (ou esgotar as
  // tentativas), evitando a necessidade de F5 para o botão seguinte destravar.
  const refetchUntilStatus = async (
    filterDate: string,
    ops: string,
    targetStatus: string,
    attempts = 6,
    intervalMs = 2000
  ) => {
    for (let i = 0; i < attempts; i++) {
      const { data } = await refetch();
      const item = (data ?? []).find(
        (c) => c.date_to_checkout.slice(0, 10) === filterDate && c.ops === ops
      );
      if (item?.status === targetStatus) {
        return;
      }
      if (i < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
    }
  };

  const confirmCheckoutMutation = useMutation({
    mutationFn: async ({
      filterDate,
      ops,
    }: {
      filterDate: string;
      ops: string;
    }) => {
      if (!xamaDbName || !user?.email) {
        throw new Error("Necessário e-mail do gestor e DBName");
      }
      return entregoCheckoutService.confirmCheckout({
        dbName: xamaDbName,
        filterDate,
        userInfo: { email: user.email },
        ops,
        // Fluxo padrão (botão WhatsApp): registra a oferta e envia a mensagem.
        offerAction: "padrao",
      });
    },
    onSuccess: (_data, variables) => {
      refetchUntilStatus(variables.filterDate, variables.ops, "confirmed");
    },
  });

  // TEMP-SPLIT-OFERTA: envio ativo pós-registro. Mesmo endpoint do registro;
  // o backend filtra franquias do piloto e quem já aceitou a oferta.
  // PILOTO ENCERRADO (2026-10-02): a página não liga mais esta mutation a nenhum
  // botão (ver index.tsx). Mantida para reativar o piloto se preciso.
  const enviarOfertasMutation = useMutation({
    mutationFn: async ({
      filterDate,
      ops,
    }: {
      filterDate: string;
      ops: string;
    }) => {
      if (!xamaDbName || !user?.email) {
        throw new Error("Necessário e-mail do gestor e DBName");
      }
      return entregoCheckoutService.confirmCheckout({
        dbName: xamaDbName,
        filterDate,
        userInfo: { email: user.email },
        ops,
        offerAction: "pilotoEnvio",
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        refetch();
      }, 3000);
    },
  });

  const startDocMutation = useMutation({
    mutationFn: async ({
      filterDate,
      ops,
    }: {
      filterDate: string;
      ops: string;
    }) => {
      if (!xamaDbName || !user?.email) {
        throw new Error("Necessário e-mail do gestor e DBName");
      }

      return entregoCheckoutService.startDoc({
        dbName: xamaDbName,
        filterDate,
        userInfo: { email: user.email },
        ops,
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        refetch();
      }, 3000);
    },
  });

  const submitRemessaMutation = useMutation({
    mutationFn: async ({
      filterDate,
      ops,
    }: {
      filterDate: string;
      ops: string;
    }) => {
      if (!xamaDbName || !user?.email) {
        throw new Error("Necessário e-mail do gestor e DBName");
      }

      return entregoCheckoutService.submitRemessa({
        dbName: xamaDbName,
        filterDate,
        userInfo: { email: user.email },
        ops,
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        refetch();
      }, 3000);
    },
  });

  return {
    checkoutList,
    franquiasPausadas,
    // isPending (e não isLoading) porque cobre também o caso de query ainda sem
    // dado; isLoading do v5 é `isPending && isFetching`, que volta false em
    // qualquer janela sem fetch em voo e deixaria o estado vazio vazar na tela.
    isLoading: isPending,
    xamaDbName,
    confirmCheckout: confirmCheckoutMutation.mutate,
    enviarOfertas: enviarOfertasMutation.mutate,
    startDoc: startDocMutation.mutate,
    submitRemessa: submitRemessaMutation.mutate,
    isConfirming: confirmCheckoutMutation.isPending,
    isEnviandoOfertas: enviarOfertasMutation.isPending,
    isStartingDoc: startDocMutation.isPending,
    isSubmittingRemessa: submitRemessaMutation.isPending,
    refetch,
  };
}
